// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertLength,
  convertPressure,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
} from '../../../../../../plugins/general';
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  getOperationalAlert,
  isEquipmentRunning,
  operationModeToText,
  overallStatusToTagType,
  overallStatusToText,
  permissiveStatusToIndicatorStatus,
} from '../../../../../../plugins/crusher';
import {
  CLAMP_PRESSURE_RANGE,
  TRAMP_PRESSURE_RANGE,
} from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import {
  LengthType,
  MeasurementType,
  PressureType,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Components
import { AlertLine } from '../../../../../../layout/icons';
import { AlertType } from '../../../../../../layout/elements/Alert';
import { Button, ButtonType } from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import { Drawer, DrawerSize } from '../../../../../../layout/elements/Drawer';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
// import { ParamIncrementor } from '../../../../../layout/elements/ParamIncrementor';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
// import { SwitchWithText } from '../../../../../layout/elements/SwitchWithText';
import { Tag } from '../../../../../../layout/elements/Tag';
import {
  Title,
  TitleSize,
  TitleWeight,
} from '../../../../../../layout/elements/Title';

// Component
import { HydraulicUnitProps } from './HydraulicUnit.types';
import {
  BorderBottom,
  CSSValueContainer,
  CSSettingContainer,
  ClampSpeedometerContainer,
  CurrentCssContainer,
  // ExecuteCommandButtonContainer,
  FooterContainer,
  NumberTeethContainer,
  OperationModeContainer,
  OverallContainer,
  OverallStatusContainer,
  ParagraphUnitContainer,
  PermissiveContainer,
  PermissiveItem,
  PermissiveListFirst,
  PermissiveListSecond,
  PermissiveTitleContainer,
  // StatusBowlAdjustment,
  TankTempContainer,
  // TargetValueContainer,
  TrampSpeedometerContainer,
  // ParamIncrementorContainer,
} from './HydraulicUnit.style';

function HydraulicUnit({
  className,
  isDrawerOpen,
  onCloseDrawer,
  unitData,
  edgeStatus,
  testId,
}: HydraulicUnitProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  const alertText: string | null = getOperationalAlert(
    edgeStatus?.edgeStatus,
    unitData?.timestamp,
    unitData?.readStatus,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  const userLengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);
  const userPressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);

  const renderPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={permissiveName}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.hydraulic.drawer.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      size={DrawerSize.lg}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
    >
      <OverallContainer>
        {/* CS Settings & Bowl Adjustment */}
        <BorderBottom>
          <Row>
            <Col
              cols={24} // @TODO: Change this to 12 when add controls
            >
              <CSSettingContainer data-testid={`${testId}-cs-setting-container`}>
                <Row>
                  <Paragraph weight={ParagraphWeight.bold}>
                    {t('view.crusher.csSetting')}
                  </Paragraph>
                </Row>
                <Row>
                  <Col>
                    <CurrentCssContainer data-testid={`${testId}-current-css-container`}>
                      <Paragraph
                        size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                        weight={ParagraphWeight.bold}
                      >
                        {t('view.crusher.hydraulic.drawer.currentCss')}
                      </Paragraph>
                      <CSSValueContainer>
                        <Title
                          size={TitleSize.xxs}
                          weight={TitleWeight.medium}
                        >
                          {
                            _.isNil(unitData?.csSetting)
                              ? DASHBOARD_NULLISH_TEXT
                              : formatNumber(
                                convertLength(unitData?.csSetting, userLengthUnit).value,
                                2,
                              )
                          }
                        </Title>
                        {
                          !_.isNil(unitData?.csSetting) && (
                            userLengthUnit === LengthType.inch
                              ? (
                                <Title
                                  size={TitleSize.xxs}
                                  weight={TitleWeight.medium}
                                  color={
                                    theme.color.crusherDashboard.hydraulicUnitDrawer
                                      .unitTextColor
                                  }
                                >
                                  {userLengthUnit}
                                </Title>
                              ) : (
                                <ParagraphUnitContainer>
                                  <Paragraph
                                    weight={ParagraphWeight.bold}
                                    color={
                                    theme.color.crusherDashboard.hydraulicUnitDrawer
                                      .unitTextColor
                                    }
                                  >
                                    {userLengthUnit}
                                  </Paragraph>
                                </ParagraphUnitContainer>
                              )
                          )
                        }
                      </CSSValueContainer>
                    </CurrentCssContainer>
                  </Col>
                </Row>
                {/* @TODO: back this flow when add control */}
                {/* <TargetValueContainer> */}
                {/*  <Paragraph */}
                {/*    size={ParagraphSize.sm} */}
                {/*    weight={ParagraphWeight.bold} */}
                {/*  > */}
                {/*    {t('view.crusher.hydraulic.drawer.targetValue')} */}
                {/*  </Paragraph> */}
                {/*  <ParamIncrementorContainer> */}
                {/*    <ParamIncrementor */}
                {/*      testId="param-incrementor-hydraulic-unit" */}
                {/*      decimalPlaces={2} */}
                {/*      unit="mm" */}
                {/*      value={data.csSetting} */}
                {/*      maxValue={5} */}
                {/*      minValue={0} */}
                {/*      onChange={handleParamChange} */}
                {/*      incrementStep={0.1} */}
                {/*      block */}
                {/*    /> */}
                {/*  </ParamIncrementorContainer> */}
                {/* </TargetValueContainer> */}
                {/* <Button */}
                {/*  disabled */}
                {/*  onClick={() => alert('Execute command was clicked!')} */}
                {/* > */}
                {/*  {t('common.executeCommand')} */}
                {/* </Button> */}
              </CSSettingContainer>
            </Col>
            {/* <Col cols={12}> */}
            {/*  <BowlAdjustmentContainer> */}
            {/*    <Paragraph weight={ParagraphWeight.bold}> */}
            {/*      {t('view.crusher.hydraulic.drawer.bowlAdjustment')} */}
            {/*    </Paragraph> */}
            {/*    <StatusBowlAdjustment> */}
            {/*      <Paragraph */}
            {/*        size={ParagraphSize.sm} */}
            {/*        weight={ParagraphWeight.bold} */}
            {/*      > */}
            {/*        {t('common.status')} */}
            {/*      </Paragraph> */}
            {/*      <SwitchWithText */}
            {/*        testId="status-switch-hydraulic-unit" */}
            {/*        leftText={`${t('common.close')}`} */}
            {/*        rightText={`${t('common.open')}`} */}
            {/*        onChange={handleSwitchChange} */}
            {/*        checked */}
            {/*      /> */}
            {/*    </StatusBowlAdjustment> */}
            {/*    <ExecuteCommandButtonContainer> */}
            {/*      <Button */}
            {/*        disabled */}
            {/*        onClick={() => alert('Execute command was clicked!')} */}
            {/*      > */}
            {/*        {t('common.executeCommand')} */}
            {/*      </Button> */}
            {/*    </ExecuteCommandButtonContainer> */}
            {/*  </BowlAdjustmentContainer> */}
            {/* </Col> */}
          </Row>
        </BorderBottom>
        {/* Overall Status & Clamp Pressure / Tank Temperature & Tramp Pressure */}
        <BorderBottom>
          <Row>
            <Col xs={24} xl={12}>
              <OverallStatusContainer data-testid={`${testId}-overall-status-container`}>
                <Paragraph
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  weight={ParagraphWeight.bold}
                >
                  {t('view.crusher.overallStatus')}
                </Paragraph>
                <Tag
                  text={overallStatusToText(unitData?.hydraulicOverallStatus)}
                  type={
                    overallStatusToTagType(
                      unitData?.hydraulicOverallStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                />
              </OverallStatusContainer>
              <ClampSpeedometerContainer data-testid={`${testId}-clamp-speedometer-container`}>
                <Speedometer
                  title={`${t('view.crusher.hydraulic.clampPressure')}`}
                  unit={userPressureUnit}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  minValue={_.isNil(unitData?.hydraulicClampPressure)
                    ? null : CLAMP_PRESSURE_RANGE(userPressureUnit).minValue}
                  maxValue={_.isNil(unitData?.hydraulicClampPressure)
                    ? null : CLAMP_PRESSURE_RANGE(userPressureUnit).maxValue}
                  value={
                    convertPressure(unitData?.hydraulicClampPressure, userPressureUnit).value
                  }
                  thresholds={{
                    lowAlarm: convertPressure(
                      unitData?.spClampPressureLowAlarm,
                      userPressureUnit,
                    ).value,
                  }}
                />
              </ClampSpeedometerContainer>
            </Col>
            <Col xs={24} xl={12}>
              <TankTempContainer data-testid={`${testId}-tank-temp-container`}>
                <Paragraph
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  weight={ParagraphWeight.bold}
                >
                  {t('view.crusher.hydraulic.tankTemp')}
                </Paragraph>
                <Tag
                  text={overallStatusToText(unitData?.hydraulicTankTempStatus)}
                  type={
                    overallStatusToTagType(
                      unitData?.hydraulicTankTempStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                />
              </TankTempContainer>
              <TrampSpeedometerContainer data-testid={`${testId}-tramp-speedometer-container`}>
                <Speedometer
                  title={`${t('view.crusher.hydraulic.trampPressure')}`}
                  unit={userPressureUnit}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  minValue={_.isNil(unitData?.hydraulicTrampPressure)
                    ? null : TRAMP_PRESSURE_RANGE(userPressureUnit).minValue}
                  maxValue={_.isNil(unitData?.hydraulicTrampPressure)
                    ? null : TRAMP_PRESSURE_RANGE(userPressureUnit).maxValue}
                  value={
                    convertPressure(unitData?.hydraulicTrampPressure, userPressureUnit).value
                  }
                  thresholds={{
                    lowAlarm: convertPressure(
                      unitData?.spTrampPressureLowAlarm,
                      userPressureUnit,
                      2,
                    ).value,
                  }}
                />
              </TrampSpeedometerContainer>
            </Col>
          </Row>
        </BorderBottom>
        {/* Number of Teeth & Operation Mode */}
        <BorderBottom>
          <Row>
            <Col cols={12}>
              <NumberTeethContainer data-testid={`${testId}-number-teeth-container`}>
                <Paragraph
                  align={ParagraphAlign.center}
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  weight={ParagraphWeight.bold}
                  color={theme.color.crusherDashboard.hydraulicUnitDrawer.numberTeethTitleTextColor}
                >
                  {t('view.crusher.hydraulic.drawer.teethTitle')}
                </Paragraph>
                <Paragraph
                  align={ParagraphAlign.center}
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  color={theme.color.crusherDashboard.hydraulicUnitDrawer.numberTeethValueTextColor}
                >
                  {formatNumber(unitData?.numberOfTeeth)}
                </Paragraph>
              </NumberTeethContainer>
            </Col>
            <Col cols={12}>
              <OperationModeContainer data-testid={`${testId}-operation-mode-container`}>
                <Paragraph
                  align={ParagraphAlign.center}
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  color={
                    theme.color.crusherDashboard.hydraulicUnitDrawer.operationModelTitleTextColor
                  }
                  weight={ParagraphWeight.bold}
                >
                  {t('view.crusher.hydraulic.drawer.operationModeTitle')}
                </Paragraph>
                <Paragraph
                  align={ParagraphAlign.center}
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  color={
                    theme.color.crusherDashboard.hydraulicUnitDrawer.operationModelStatusTextColor
                  }
                >
                  {operationModeToText(
                    unitData?.operationModeLocalRemote,
                    unitData?.operationModeOperateService,
                  )}
                </Paragraph>
              </OperationModeContainer>
            </Col>
          </Row>
        </BorderBottom>
        {/* Permissives & Bowl Movement */}
        <Row>
          <Col>
            <PermissiveContainer>
              <PermissiveTitleContainer>
                <StatusIndicator
                  size={StatusIndicatorSize.lg}
                  text={t('view.crusher.hydraulic.drawer.permissive')}
                  status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.hydraulicPermissive,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                />
              </PermissiveTitleContainer>
              <Row>
                <Col xs={24} xl={12}>
                  <PermissiveListFirst>
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.pumpOverPressure'),
                      unitData?.pumpOverPressure,
                    )}
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.highOilTemp'),
                      unitData?.highOilTemp,
                    )}
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.hydPumpRunning'),
                      unitData?.hydPumpRunningOver1Hour,
                    )}
                  </PermissiveListFirst>
                </Col>
                <Col xs={24} xl={12}>
                  <PermissiveListSecond>
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.eStop'),
                      unitData?.eStop,
                    )}
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.systemControlPower'),
                      unitData?.systemControlPower,
                    )}
                    {renderPermissive(
                      t('view.crusher.hydraulic.drawer.oilLevelLow'),
                      unitData?.oilLevelLow,
                    )}
                  </PermissiveListSecond>
                </Col>
              </Row>
            </PermissiveContainer>
          </Col>

        </Row>
      </OverallContainer>
      <Row>
        <FooterContainer data-testid={`${testId}-footer-container`}>
          <Paragraph
            size={isXs ? ParagraphSize.sm : undefined}
            weight={ParagraphWeight.bold}
          >
            {t('view.crusher.hydraulic.drawer.solenoidChart')}
          </Paragraph>
          <Button
            type={ButtonType.secondary}
            disabled
            block={false}
            onClick={() => {}}
          >
            {t('view.crusher.hydraulic.drawer.openTheChart')}
          </Button>
        </FooterContainer>
      </Row>
    </Drawer>
  );
}

export { HydraulicUnit };
